import { debounce } from "lodash-es";
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { TextFieldProps } from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";

import { useDispatch, useSelector } from "../../store";
import { ReduxFormProps } from "../../types/ReduxForm";
import StyledTextField from "../styled/TextField";

export type ReduxTextProps = TextFieldProps &
  ReduxFormProps & {
    disabledText?: string;
    validator?: (val: string) => string;
  };

export const ReduxTextField = ({
  getter,
  setter,
  errorHandler,
  path,
  disabledText,
  disabled,
  defaultValue = "",
  validator = () => "",
  required = false,
  ...props
}: ReduxTextProps): ReactElement => {
  const dispatch = useDispatch();
  const formValue = useSelector(getter(path, defaultValue)) as string;
  const [inputValue, setInputValue] = useState<string>(
    formValue === null ? "" : formValue,
  );
  const [errorMessage, setErrorMessage] = useState<string>("");
  const emptyButRequired = useMemo(
    () => required && !inputValue,
    [required, inputValue],
  );

  useEffect(() => {
    if (emptyButRequired) {
      setErrorMessage("This field is required");
    } else if (errorMessage === "This field is required") {
      setErrorMessage("");
    }
  }, [emptyButRequired]);

  useEffect(() => {
    if (errorHandler) {
      dispatch(errorHandler({ path, error: errorMessage }));
    }
  }, [dispatch, errorMessage, path]);

  const delayedOnSave = useCallback(
    debounce((val) => {
      dispatch(setter({ path, value: val }));

      if (!required && val.length === 0) {
        setErrorMessage("");
        return;
      }

      if (val.length !== 0) {
        setErrorMessage(validator(val));
      }
    }, 150),
    [setter, path],
  );

  const onChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(evt.target.value);
    delayedOnSave(evt.target.value);
  };

  const onBlur = (): void => {
    dispatch(setter({ path, value: inputValue }));
    if (inputValue.length !== 0) {
      setErrorMessage(validator(inputValue));
    }
  };

  if (disabled && !!disabledText) {
    return (
      <Tooltip title={disabledText} placement={"bottom-start"}>
        <span>
          <StyledTextField
            fullWidth
            {...props}
            error={!!errorMessage.length}
            helperText={errorMessage}
            disabled={disabled}
            placeholder={"Disabled"}
            value={inputValue}
            onChange={onChange}
          />
        </span>
      </Tooltip>
    );
  }

  return (
    <StyledTextField
      {...props}
      fullWidth
      error={!!errorMessage.length}
      helperText={errorMessage}
      disabled={disabled}
      value={inputValue}
      onChange={onChange}
      onBlur={onBlur}
    />
  );
};
